/**
 * This file exposes a hook for doing live transcription using ElevenLabs's real-time service.
 *
 * Flow:
 * 1. Client (user) starts a recording.
 * 2. We immediately start recording locally and connect to ElevenLabs in the background.
 * 3. We buffer the recording until we're connected to ElevenLabs.
 * 4. Once connected, we send buffered audio and then stream in real-time.
 * 5. When the user stops recording, we flush audio, commit once, and finalize when
 *    ElevenLabs returns the committed transcript or closes the socket.
 */

'use client';

import { useRef, useState, useEffect, useCallback } from 'react';
import { RecordRTCPromisesHandler as RecordRTC, StereoAudioRecorder } from 'recordrtc';
import {
  AudioFormat,
  CommitStrategy,
  RealtimeConnection,
  RealtimeEvents,
  Scribe,
} from '@elevenlabs/client';
import { elevenLabsRealtimeTokenService } from './elevenlabs-token-service';

const ELEVENLABS_REALTIME_MODEL = 'scribe_v2_realtime';

export type RecordingStatus = 'idle' | 'recording' | 'stopping';

export interface UseLiveTranscriptionConfig {
  onStop?: (data: { transcript: string; recordingBlob: Blob | null }) => void;
  onCancel?: (data: { recordingBlob: Blob | null }) => void;
  onError?: (data: { error: Error; recordingBlob: Blob | null }) => void;
  shouldPrefetchToken?: boolean;
  /**
   * Where to get a single-use ElevenLabs token. Defaults to the web app's own route; apps
   * whose users aren't signed in to it (published chatbots) supply their own. No prefetch then.
   */
  getToken?: () => Promise<string>;
}

const SAFETY_TIMEOUT_MS = 10000;

function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  if (error && typeof error === 'object') {
    if ('message' in error && typeof error.message === 'string') {
      return new Error(error.message);
    }

    if ('error' in error && typeof error.error === 'string') {
      return new Error(error.error);
    }
  }

  return new Error('Unknown transcription error');
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }

  return btoa(binary);
}

function extractPcmFromWav(buffer: ArrayBuffer): ArrayBuffer {
  const view = new DataView(buffer);

  if (view.byteLength < 12) {
    return buffer;
  }

  const riff =
    String.fromCharCode(view.getUint8(0)) +
    String.fromCharCode(view.getUint8(1)) +
    String.fromCharCode(view.getUint8(2)) +
    String.fromCharCode(view.getUint8(3));
  const wave =
    String.fromCharCode(view.getUint8(8)) +
    String.fromCharCode(view.getUint8(9)) +
    String.fromCharCode(view.getUint8(10)) +
    String.fromCharCode(view.getUint8(11));

  if (riff !== 'RIFF' || wave !== 'WAVE') {
    return buffer;
  }

  let offset = 12;
  while (offset + 8 <= view.byteLength) {
    const chunkId =
      String.fromCharCode(view.getUint8(offset)) +
      String.fromCharCode(view.getUint8(offset + 1)) +
      String.fromCharCode(view.getUint8(offset + 2)) +
      String.fromCharCode(view.getUint8(offset + 3));
    const chunkSize = view.getUint32(offset + 4, true);
    const dataStart = offset + 8;

    if (chunkId === 'data') {
      const dataEnd = Math.min(dataStart + chunkSize, view.byteLength);
      return buffer.slice(dataStart, dataEnd);
    }

    offset = dataStart + chunkSize + (chunkSize % 2);
  }

  return buffer;
}

export function useElevenLabsRealtimeTokenPrefetch() {
  useEffect(() => {
    void elevenLabsRealtimeTokenService.prefetch();
  }, []);
}

export function useLiveTranscription(config?: UseLiveTranscriptionConfig) {
  const shouldPrefetchToken = (config?.shouldPrefetchToken ?? true) && !config?.getToken;
  const statusRef = useRef<RecordingStatus>('idle');
  const [status, setStatus] = useState<RecordingStatus>('idle');

  const [transcript, setTranscript] = useState('');
  const transcriptRef = useRef('');
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);
  const recordingBlobRef = useRef<Blob | null>(null);

  const recorderRef = useRef<RecordRTC | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const connectionRef = useRef<RealtimeConnection | null>(null);
  const isConnectedRef = useRef(false);
  const isConnectingRef = useRef(false);
  const wsOpenRef = useRef(false);
  const isStaleRef = useRef(false);
  const wasCanceledRef = useRef(false);
  const hasSentAudioRef = useRef(false);

  const audioBufferRef = useRef<ArrayBuffer[]>([]);
  const committedChunksRef = useRef<string[]>([]);
  const partialRef = useRef('');

  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onStopRef = useRef(config?.onStop);
  const onCancelRef = useRef(config?.onCancel);
  const onErrorRef = useRef(config?.onError);
  const getTokenRef = useRef(config?.getToken);
  const stopFlowRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    onStopRef.current = config?.onStop;
    onCancelRef.current = config?.onCancel;
    onErrorRef.current = config?.onError;
    getTokenRef.current = config?.getToken;
  }, [config]);

  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  useEffect(() => {
    recordingBlobRef.current = recordingBlob;
  }, [recordingBlob]);

  useEffect(() => {
    if (!shouldPrefetchToken) {
      return;
    }

    void elevenLabsRealtimeTokenService.prefetch();
  }, [shouldPrefetchToken]);

  const clearSafetyTimeout = useCallback(() => {
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
  }, []);

  const updateStatus = useCallback((nextStatus: RecordingStatus) => {
    statusRef.current = nextStatus;
    setStatus(nextStatus);
  }, []);

  const buildTranscript = useCallback(() => {
    const committedTranscript = committedChunksRef.current.join(' ').trim();
    const partialTranscript = partialRef.current.trim();
    return [committedTranscript, partialTranscript].filter(Boolean).join(' ').trim();
  }, []);

  const syncTranscriptState = useCallback(() => {
    const nextTranscript = buildTranscript();
    transcriptRef.current = nextTranscript;
    setTranscript(nextTranscript);
  }, [buildTranscript]);

  const closeConnection = useCallback(() => {
    if (connectionRef.current) {
      try {
        connectionRef.current.close();
      } catch {
        // Ignore close errors during teardown.
      }
      connectionRef.current = null;
    }

    isConnectedRef.current = false;
    isConnectingRef.current = false;
    wsOpenRef.current = false;
    isStaleRef.current = false;
    hasSentAudioRef.current = false;
  }, []);

  const sendAudioChunk = useCallback((chunk: ArrayBuffer) => {
    if (!connectionRef.current || !wsOpenRef.current) {
      return false;
    }

    try {
      connectionRef.current.send({
        audioBase64: arrayBufferToBase64(chunk),
        sampleRate: 16000,
      });
      hasSentAudioRef.current = true;
      return true;
    } catch (error) {
      console.error('Error sending audio chunk:', error);
      return false;
    }
  }, []);

  const flushBufferedAudio = useCallback(() => {
    if (!wsOpenRef.current || wasCanceledRef.current) {
      audioBufferRef.current = [];
      return;
    }

    const bufferedChunks = [...audioBufferRef.current];
    audioBufferRef.current = [];

    for (let index = 0; index < bufferedChunks.length; index += 1) {
      const chunk = bufferedChunks[index];

      if (!chunk || !wsOpenRef.current || wasCanceledRef.current) {
        audioBufferRef.current.push(...bufferedChunks.slice(index));
        break;
      }

      if (!sendAudioChunk(chunk)) {
        audioBufferRef.current.push(...bufferedChunks.slice(index));
        break;
      }
    }
  }, [sendAudioChunk]);

  const commitTranscription = useCallback(() => {
    if (!hasSentAudioRef.current || !connectionRef.current) {
      return false;
    }

    try {
      connectionRef.current.commit();
      return true;
    } catch (error) {
      console.warn('Failed to commit ElevenLabs transcription:', error);
      return false;
    }
  }, []);

  const stopCapture = useCallback(async () => {
    const currentRecorder = recorderRef.current;
    const currentMediaRecorder = mediaRecorderRef.current;
    const currentStream = streamRef.current;

    recorderRef.current = null;
    mediaRecorderRef.current = null;
    streamRef.current = null;

    if (currentMediaRecorder && currentMediaRecorder.state !== 'inactive') {
      try {
        currentMediaRecorder.stop();
      } catch (error) {
        console.error('Error stopping media recorder:', error);
      }
    }

    if (currentRecorder) {
      try {
        await currentRecorder.stopRecording();
      } catch (error) {
        console.error('Error stopping RecordRTC:', error);
      }
    }

    if (currentStream) {
      try {
        currentStream.getTracks().forEach(track => track.stop());
      } catch (error) {
        console.error('Error stopping media stream:', error);
      }
    }
  }, []);

  const transitionToIdle = useCallback(() => {
    if (statusRef.current === 'idle') {
      return;
    }

    clearSafetyTimeout();

    const finalTranscript = buildTranscript();
    transcriptRef.current = finalTranscript;
    setTranscript(finalTranscript);

    if (wasCanceledRef.current) {
      wasCanceledRef.current = false;
      onCancelRef.current?.({
        recordingBlob: recordingBlobRef.current,
      });
    } else {
      onStopRef.current?.({
        transcript: finalTranscript,
        recordingBlob: recordingBlobRef.current,
      });
    }

    updateStatus('idle');
  }, [buildTranscript, clearSafetyTimeout, updateStatus]);

  const forceComplete = useCallback(() => {
    void stopCapture();
    closeConnection();
    audioBufferRef.current = [];
    transitionToIdle();
  }, [closeConnection, stopCapture, transitionToIdle]);

  const handleError = useCallback((error: unknown) => {
    const parsedError = toError(error);
    console.error('Error:', parsedError);

    onErrorRef.current?.({
      error: parsedError,
      recordingBlob: recordingBlobRef.current,
    });
  }, []);

  const initConnection = useCallback(async () => {
    if (isConnectingRef.current || isConnectedRef.current || connectionRef.current) {
      return;
    }

    isConnectingRef.current = true;

    try {
      const token = getTokenRef.current
        ? await getTokenRef.current()
        : await elevenLabsRealtimeTokenService.getToken();

      const connection = Scribe.connect({
        token,
        modelId: ELEVENLABS_REALTIME_MODEL,
        commitStrategy: CommitStrategy.MANUAL,
        audioFormat: AudioFormat.PCM_16000,
        sampleRate: 16000,
      });

      connectionRef.current = connection;
      committedChunksRef.current = [];
      partialRef.current = '';

      connection.on(RealtimeEvents.OPEN, () => {
        isConnectingRef.current = false;
        isConnectedRef.current = true;
        wsOpenRef.current = true;

        if (wasCanceledRef.current) {
          closeConnection();
          transitionToIdle();
          return;
        }

        if (statusRef.current === 'stopping') {
          flushBufferedAudio();

          if (!hasSentAudioRef.current) {
            closeConnection();
            transitionToIdle();
            return;
          }

          if (!commitTranscription()) {
            closeConnection();
            transitionToIdle();
            return;
          }

          isStaleRef.current = true;
          return;
        }

        if (audioBufferRef.current.length > 0) {
          flushBufferedAudio();
        }
      });

      connection.on(RealtimeEvents.PARTIAL_TRANSCRIPT, transcriptData => {
        if (wasCanceledRef.current) {
          return;
        }

        if (isStaleRef.current && statusRef.current !== 'stopping') {
          return;
        }

        partialRef.current = transcriptData.text ?? '';
        syncTranscriptState();
      });

      connection.on(RealtimeEvents.COMMITTED_TRANSCRIPT, transcriptData => {
        if (wasCanceledRef.current) {
          return;
        }

        const committedText = transcriptData.text?.trim();
        if (committedText) {
          committedChunksRef.current.push(committedText);
        }

        partialRef.current = '';
        syncTranscriptState();

        if (statusRef.current === 'stopping') {
          setTimeout(() => {
            closeConnection();
            transitionToIdle();
          }, 0);
        }
      });

      connection.on(RealtimeEvents.ERROR, error => {
        if (
          statusRef.current === 'stopping' ||
          statusRef.current === 'idle' ||
          isStaleRef.current ||
          wasCanceledRef.current
        ) {
          return;
        }

        isConnectedRef.current = false;
        isConnectingRef.current = false;
        wsOpenRef.current = false;

        handleError(error);

        if (statusRef.current === 'recording') {
          void stopFlowRef.current?.();
        }
      });

      connection.on(RealtimeEvents.CLOSE, () => {
        isConnectedRef.current = false;
        isConnectingRef.current = false;
        wsOpenRef.current = false;

        if (statusRef.current === 'recording') {
          void stopFlowRef.current?.();
        } else if (statusRef.current === 'stopping') {
          transitionToIdle();
        }
      });
    } catch (error) {
      isConnectingRef.current = false;
      isConnectedRef.current = false;
      wsOpenRef.current = false;
      connectionRef.current = null;

      handleError(error);

      if (statusRef.current === 'recording') {
        void stopFlowRef.current?.();
      } else if (statusRef.current === 'stopping') {
        transitionToIdle();
      }
    }
  }, [
    closeConnection,
    commitTranscription,
    flushBufferedAudio,
    handleError,
    syncTranscriptState,
    transitionToIdle,
  ]);

  const startRecording = useCallback(async () => {
    if (statusRef.current !== 'idle') {
      return;
    }

    setTranscript('');
    setRecordingBlob(null);
    updateStatus('recording');

    isStaleRef.current = false;
    wasCanceledRef.current = false;
    audioBufferRef.current = [];
    committedChunksRef.current = [];
    partialRef.current = '';
    hasSentAudioRef.current = false;
    clearSafetyTimeout();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const recordRTC = new RecordRTC(stream, {
        type: 'audio',
        recorderType: StereoAudioRecorder,
        mimeType: 'audio/wav',
        desiredSampRate: 16000,
        numberOfAudioChannels: 1,
        bufferSize: 4096,
        audioBitsPerSecond: 16000,
        timeSlice: 250,
        ondataavailable: async blob => {
          if (
            (statusRef.current !== 'recording' && statusRef.current !== 'stopping') ||
            isStaleRef.current ||
            wasCanceledRef.current
          ) {
            return;
          }

          try {
            const buffer = extractPcmFromWav(await blob.arrayBuffer());

            if (isConnectedRef.current && wsOpenRef.current && connectionRef.current) {
              if (!sendAudioChunk(buffer)) {
                audioBufferRef.current.push(buffer);
              }
            } else {
              audioBufferRef.current.push(buffer);
            }
          } catch (error) {
            console.error('Error processing audio chunk:', error);
            handleError(error);
          }
        },
      });

      const mediaRecorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];

      mediaRecorder.ondataavailable = event => {
        chunks.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const completeBlob = new Blob(chunks, { type: 'audio/wav' });
        setRecordingBlob(completeBlob);
      };

      mediaRecorder.start();
      await recordRTC.startRecording();

      mediaRecorderRef.current = mediaRecorder;
      recorderRef.current = recordRTC;

      void initConnection();
    } catch (error) {
      const parsedError = toError(error);
      console.error('Error starting recording:', parsedError);
      handleError(parsedError);
      await stopCapture();
      updateStatus('idle');
    }
  }, [clearSafetyTimeout, handleError, initConnection, sendAudioChunk, stopCapture, updateStatus]);

  const stopRecording = useCallback(async () => {
    if (statusRef.current !== 'recording') {
      return;
    }

    updateStatus('stopping');

    try {
      await stopCapture();

      if (isConnectedRef.current && wsOpenRef.current) {
        flushBufferedAudio();

        if (!hasSentAudioRef.current) {
          closeConnection();
          transitionToIdle();
          return;
        }

        if (!commitTranscription()) {
          closeConnection();
          transitionToIdle();
          return;
        }

        isStaleRef.current = true;
      } else if (isConnectingRef.current) {
        isStaleRef.current = true;
      } else {
        closeConnection();
        transitionToIdle();
      }
    } catch (error) {
      handleError(error);
      forceComplete();
    }
  }, [
    closeConnection,
    commitTranscription,
    flushBufferedAudio,
    forceComplete,
    handleError,
    stopCapture,
    transitionToIdle,
    updateStatus,
  ]);

  const cancelRecording = useCallback(async () => {
    if (statusRef.current === 'idle') {
      return;
    }

    wasCanceledRef.current = true;
    isStaleRef.current = true;

    if (statusRef.current === 'recording') {
      updateStatus('stopping');
    }

    try {
      await stopCapture();
      audioBufferRef.current = [];
      closeConnection();
      transitionToIdle();
    } catch (error) {
      handleError(error);
      forceComplete();
    }
  }, [closeConnection, forceComplete, handleError, stopCapture, transitionToIdle, updateStatus]);

  const resetRecording = useCallback(() => {
    if (statusRef.current === 'idle') {
      committedChunksRef.current = [];
      partialRef.current = '';
      transcriptRef.current = '';
      setTranscript('');
      setRecordingBlob(null);
    }
  }, []);

  useEffect(() => {
    stopFlowRef.current = stopRecording;
  }, [stopRecording]);

  useEffect(() => {
    if (status !== 'stopping') {
      clearSafetyTimeout();
      return;
    }

    safetyTimeoutRef.current = setTimeout(() => {
      console.warn('[use-live-transcription] Safety timeout reached. Forcing idle.');
      forceComplete();
    }, SAFETY_TIMEOUT_MS);

    return clearSafetyTimeout;
  }, [clearSafetyTimeout, forceComplete, status]);

  useEffect(() => {
    return () => {
      clearSafetyTimeout();
      void stopCapture();
      closeConnection();
    };
  }, [clearSafetyTimeout, closeConnection, stopCapture]);

  return {
    status,
    isRecording: status === 'recording',
    transcript,
    recordingBlob,
    startRecording,
    stopRecording,
    cancelRecording,
    resetRecording,
  };
}
